import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Zap, Crown, CheckCircle, ArrowRight } from 'lucide-react';

export default function UpgradeModal({ 
  isOpen, 
  onClose, 
  feature, 
  currentPlan, 
  requiredPlan,
  requiredPriceMonthly,
  requiredPriceYearly,
  availablePlans, 
  role 
}) {
  const [billingCycle, setBillingCycle] = useState('monthly');

  if (!isOpen) return null;

  const featureLabels = {
    messaging: 'Direct Messaging',
    pitch_deck_upload: 'Pitch Deck Upload',
    pitch_deck_access: 'Pitch Deck Access',
    nda_gated_content: 'NDA-Protected Content',
    pitch_session_booking: 'Pitch Session Booking',
    unlimited_matches: 'Unlimited Matches',
    unlimited_matching: 'Unlimited Matching',
    advanced_filters: 'Advanced Filters',
    cofounder_search: 'Co-founder Search',
    visibility_boost: 'Profile Visibility Boost',
    early_access: 'Early Access to Deals',
    curated_dealflow: 'Curated Deal Flow',
  };

  const getPrice = (plan) => {
    if (plan.monthly_price_usd === 0) return 'Free';
    const price = billingCycle === 'monthly' 
      ? plan.monthly_price_usd 
      : Math.round(plan.yearly_price_usd / 12);
    return `$${price}/mo`;
  };

  const getPricingPath = () => {
    if (role === 'founder') return '/pricing/founder';
    if (role === 'investor') return '/pricing/investor';
    if (role === 'cofounder') return '/pricing/cofounder';
    return '/pricing';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="bg-white border border-slate-200 shadow-xl max-w-lg w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X size={20} />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <Zap className="text-blue-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-blue-900 mb-2">Upgrade Required</h2>
          <p className="text-blue-700">
            <span className="text-blue-600 font-medium">{featureLabels[feature] || feature}</span> is not available on your current plan.
          </p>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-700 text-sm">Current Plan</span>
            <span className="text-blue-900 font-medium">{currentPlan || 'Free'}</span>
          </div>
        </div>

        {/* Show required plan directly if available */}
        {requiredPlan && (
          <div className="space-y-3 mb-6">
            <p className="text-blue-800 text-sm font-medium">Upgrade to unlock this feature:</p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="text-blue-600" size={24} />
                  <div>
                    <span className="text-blue-900 font-semibold text-lg">{requiredPlan}</span>
                    <p className="text-blue-700 text-sm">Includes {featureLabels[feature] || feature}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-blue-600 font-bold text-xl">
                    ${requiredPriceMonthly || 49}/mo
                  </span>
                  {requiredPriceYearly && (
                    <p className="text-blue-700 text-xs">or ${requiredPriceYearly}/year</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fallback to availablePlans if no requiredPlan */}
        {!requiredPlan && availablePlans && availablePlans.length > 0 && (
          <div className="space-y-3 mb-6">
            <p className="text-blue-800 text-sm font-medium">Plans that include this feature:</p>
            {availablePlans.map((plan) => (
              <div
                key={plan.plan_name}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-blue-600" size={18} />
                  <span className="text-blue-900">{plan.plan_name}</span>
                </div>
                <span className="text-blue-600 font-semibold">{getPrice(plan)}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Link to="/account" onClick={onClose}>
            <Button className="btn-primary w-full">
              Upgrade Now
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
          <Button variant="ghost" onClick={onClose} className="text-blue-700 hover:text-blue-900">
            Maybe Later
          </Button>
        </div>
      </Card>
    </div>
  );
}
